
import Vue from "vue";
import { mapGetters, mapMutations, mapActions } from "vuex";
import {
  GET_USER_DETAILS,
  USER_DETAILS_BY_ID,
  USER_PROFILE_BY_ID
} from "@/store/modules/auth/constants";
import { ROOT_NOTIFICATION } from "@/store/modules/root/constants";
import {
  AI_INTERVIEW_INVITE_BY_ID,
  INIT_AI_INTERVIEW_INVITES
} from "@/store/modules/recruiter_ai_interviews/constants";
import { get_latest_interview_report } from "@/store/apis/ml_interview";

export default Vue.extend({
  name: "RecruiterInterviewGuidelines",
  data() {
    return {
      fallbackAvatar: require("@/assets/images/female-bot.png"),
      candidateUser: null as any,
      candidateProfile: null as any,
      interviewDetailsTop: [
        { label: "Role", value: "Software Engineer" },
        { label: "Candidate", value: "-" },
        { label: "Experience Level", value: "8 Years" }
      ],
      interviewDetailsBottom: [
        { label: "Format", value: "Technical Interview" },
        { label: "Duration", value: "60 Minutes" },
        { label: "Interview Mode", value: "On Portal" }
      ],
      candidateSummary: [
        { label: "Current/Last Role", value: "Associate Software Engineer" },
        { label: "Highest Qualification", value: "Masters in Computer Science" },
        { label: "Graduation Year", value: "2014" },
        { label: "University / Institution", value: "Harvard University" }
      ],
      skills: ["HTML", "CSS", "SQL", "Python", "JavaScript", "C#", "Ruby", "Go"],
      guidelineOverview: "",
      suggestedQuestions: [] as string[],
      criticalProbes: [] as Array<{
        topic: string;
        why: string;
        suggestedQuestion: string;
        doNotAccept: string;
      }>,
      generalInterviewDos: [] as Array<{ title: string; description: string }>,
      generalInterviewDonts: [] as Array<{ title: string; description: string }>,
      interviewSections: [] as Array<{
        sectionName: string;
        sectionType: string;
        description: string;
        questions: Array<{
          question: string;
          competency: string;
          whatToLookFor: string;
          followUp: string;
        }>;
        scoringGuide: Array<{ label: string; text: string }>;
      }>,
      scoringRubric: [] as Array<{
        area: string;
        bands: Array<{ label: string; text: string }>;
      }>,
      redFlagsToWatch: [] as string[],
      biasWarnings: [] as string[],
      questionsAlreadyCovered: [] as string[],
      questionBankItems: [] as string[]
    };
  },
  computed: {
    ...mapGetters("auth", {
      user: GET_USER_DETAILS
    }),
    ...mapGetters("recruiter_ai_interviews", {
      inviteById: AI_INTERVIEW_INVITE_BY_ID
    }),
    invite(this: any): any {
      const id = (this.$route.query.inviteId as string) || "";
      if (!id) return null;
      return (this as any).inviteById(id);
    },
    candidateName(this: any): string {
      const profileName = this.candidateProfile?.profile?.personal_details?.name;
      if (profileName) {
        return Object.values(profileName)
          .filter((v: any) => v)
          .join(" ");
      }
      return this.invite?.candidate_name || "Ahmad Al-Mansouri";
    },
    candidateRole(this: any): string {
      const workHistory = this.candidateProfile?.profile?.work_history_detailed;
      if (workHistory && Object.values(workHistory).length > 0) {
        return (Object.values(workHistory)[0] as any).job_title || "Candidate";
      }
      return this.invite?.candidate_role || "Sr. Software Engineer";
    },
    candidateExperienceLevel(this: any): string {
      return this.candidateProfile?.profile?.total_experience || "-";
    },
    candidateAvatar(this: any): string {
      return (
        this.candidateUser?.avatar_uri ||
        this.invite?.avatar_uri ||
        this.user?.avatar_uri ||
        this.fallbackAvatar
      );
    },
    highestQualification(this: any): string {
      const education = this.candidateProfile?.profile?.education_detailed;
      if (!education || Object.values(education).length <= 0) return "-";
      return (Object.values(education)[0] as any).degree_title || "-";
    },
    universityInstitution(this: any): string {
      const education = this.candidateProfile?.profile?.education_detailed;
      if (!education || Object.values(education).length <= 0) return "-";
      return (Object.values(education)[0] as any).organization_name || "-";
    },
    graduationYear(this: any): string {
      const education = this.candidateProfile?.profile?.education_detailed;
      if (!education || Object.values(education).length <= 0) return "-";
      const timePeriod = (Object.values(education)[0] as any).time_period || "";
      const years = String(timePeriod).match(/\b(19|20)\d{2}\b/g);
      return years && years.length ? years[years.length - 1] : "-";
    }
  },
  async created(this: any) {
    await this.initInvites();
    await this.loadCandidateData();
    this.syncSummaryData();
    await this.hydrateGuidelines();
  },
  watch: {
    "$route.query.inviteId": {
      immediate: false,
      async handler(this: any) {
        await this.loadCandidateData();
        this.syncSummaryData();
        await this.hydrateGuidelines();
      }
    }
  },
  methods: {
    ...mapActions("auth", {
      fetchUserById: USER_DETAILS_BY_ID,
      fetchProfileById: USER_PROFILE_BY_ID
    }),
    ...mapActions("recruiter_ai_interviews", {
      initInvites: INIT_AI_INTERVIEW_INVITES
    }),
    ...mapMutations({
      set_root_notification: ROOT_NOTIFICATION
    }),
    notify(this: any, message: string) {
      this.set_root_notification(message);
    },
    async loadCandidateData(this: any) {
      const candidateId = this.invite?.candidate_id;
      if (!candidateId) return;
      const [user, profile] = await Promise.all([
        this.fetchUserById(candidateId),
        this.fetchProfileById(candidateId)
      ]);
      this.candidateUser = user;
      this.candidateProfile = profile;
    },
    syncSummaryData(this: any) {
      const detailsTop = [...this.interviewDetailsTop];
      detailsTop[0] = { ...detailsTop[0], value: this.candidateRole || "-" };
      detailsTop[1] = { ...detailsTop[1], value: this.candidateName || "-" };
      detailsTop[2] = {
        ...detailsTop[2],
        value: this.candidateExperienceLevel || "-"
      };
      this.interviewDetailsTop = detailsTop;

      this.candidateSummary = [
        { label: "Current/Last Role", value: this.candidateRole || "-" },
        { label: "Highest Qualification", value: this.highestQualification },
        { label: "Graduation Year", value: this.graduationYear },
        { label: "University / Institution", value: this.universityInstitution }
      ];
    },
    async hydrateGuidelines(this: any) {
      try {
        const interviewType = this.$route.name?.includes("person")
          ? "person"
          : "ai";
        const candidateId = this.invite?.candidate_id;
        const jobId = Number(this.invite?.job_id || 0) || null;
        let brief: any = {};
        let savedQuestionBank: any[] = [];
        if (candidateId) {
          const reportResp = await get_latest_interview_report(
            Number(candidateId),
            interviewType as "ai" | "person",
            jobId
          );
          brief = reportResp?.data?.data?.interviewer_brief || {};
          savedQuestionBank = reportResp?.data?.data?.question_bank || [];
        }

        if (!brief || Object.keys(brief).length <= 0) return;
        this.guidelineOverview = String(brief?.overview || brief?.summary || "").trim();
        this.suggestedQuestions = Array.isArray(brief?.questions_to_ask)
          ? brief.questions_to_ask
              .map((q: any) =>
                `${q?.text || "Question"}${q?.scoring_guide ? ` - ${q.scoring_guide}` : ""}`
              )
          : [];
        this.criticalProbes = Array.isArray(brief?.critical_probes)
          ? brief.critical_probes.map((probe: any) => ({
              topic: String(probe?.topic || "Probe"),
              why: String(probe?.why || ""),
              suggestedQuestion: String(probe?.suggested_question || ""),
              doNotAccept: String(probe?.do_not_accept || "")
            }))
          : [];
        this.generalInterviewDos = Array.isArray(brief?.general_interview_guidelines?.dos)
          ? brief.general_interview_guidelines.dos
              .map((item: any) => ({
                title: String(item?.title || "").trim(),
                description: String(item?.description || "").trim()
              }))
              .filter((item: any) => item.title || item.description)
          : [];
        this.generalInterviewDonts = Array.isArray(brief?.general_interview_guidelines?.donts)
          ? brief.general_interview_guidelines.donts
              .map((item: any) => ({
                title: String(item?.title || "").trim(),
                description: String(item?.description || "").trim()
              }))
              .filter((item: any) => item.title || item.description)
          : [];
        this.interviewSections = Array.isArray(brief?.interview_sections)
          ? brief.interview_sections
              .map((section: any) => {
                const rawScoringGuide = section?.scoring_guide || {};
                const scoringGuide =
                  rawScoringGuide && typeof rawScoringGuide === "object"
                    ? Object.keys(rawScoringGuide)
                        .map((label: string) => ({
                          label,
                          text: String(rawScoringGuide[label] || "").trim()
                        }))
                        .filter((item: any) => !!item.text)
                    : [];
                const questions = Array.isArray(section?.questions)
                  ? section.questions
                      .map((question: any) => ({
                        question: String(question?.question || "").trim(),
                        competency: String(question?.competency || "").trim(),
                        whatToLookFor: String(question?.what_to_look_for || "").trim(),
                        followUp: String(question?.follow_up || "").trim()
                      }))
                      .filter((item: any) => !!item.question)
                  : [];
                return {
                  sectionName: String(section?.section_name || "Section").trim(),
                  sectionType: String(section?.section_type || "fixed").trim(),
                  description: String(section?.description || "").trim(),
                  questions,
                  scoringGuide
                };
              })
              .filter((item: any) => !!item.sectionName)
          : [];
        this.scoringRubric =
          brief?.scoring_rubric && typeof brief.scoring_rubric === "object"
            ? Object.keys(brief.scoring_rubric)
                .map((area: string) => {
                  const rubric = brief.scoring_rubric[area] || {};
                  if (!rubric || typeof rubric !== "object") return null;
                  const bands = Object.keys(rubric)
                    .map((band: string) => ({
                      label: band,
                      text: String(rubric[band] || "")
                    }))
                    .filter((b: any) => !!b.text);
                  if (!bands.length) return null;
                  return {
                    area: area.replace(/_/g, " ").toUpperCase(),
                    bands
                  };
                })
                .filter((item: any) => !!item)
            : [];
        this.redFlagsToWatch = Array.isArray(brief?.red_flags_to_watch)
          ? brief.red_flags_to_watch.map((item: any) => String(item || "")).filter((v: string) => !!v)
          : [];
        this.biasWarnings = Array.isArray(brief?.bias_warnings)
          ? brief.bias_warnings.map((item: any) => String(item || "")).filter((v: string) => !!v)
          : [];
        this.questionsAlreadyCovered = Array.isArray(brief?.questions_already_covered)
          ? brief.questions_already_covered
              .map((item: any) => String(item || "").trim())
              .filter((v: string) => !!v)
          : [];
        this.questionBankItems = Array.isArray(savedQuestionBank)
          ? savedQuestionBank
              .map(
                (q: any) =>
                  `${q?.id || "-"} - ${q?.text || "Question"}${
                    q?.competency ? ` (${q.competency})` : ""
                  }`
              )
          : [];
        if (this.criticalProbes.length) {
          this.skills = this.criticalProbes.map((p: any) => p.topic).filter((v: string) => !!v);
        }
      } catch (e) {
        // keep fallback guideline content
      }
    }
  }
});
